import * as React from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Divider, Grid, Paper, TextField,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import GetDepartmentTypes from '../../../lib/SWRfetchers/getDepartments';
import type ILabelEnumPair from '../../global/GlobalInterfaces';

interface IDestinationBin {
  name: string,
  barcode: string,
  isBin: boolean,
  sequence: number | undefined,
  stationType: ILabelEnumPair | undefined,
  departmentType: ILabelEnumPair | undefined,
}

const UpsertBins: React.FC<unknown> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { departments } = GetDepartmentTypes();

  const { binId: binIdParam } = useParams();

  const [showErrors, setShowErrors] = React.useState(false);
  const [stations, setStations] = React.useState<ILabelEnumPair[] | undefined>(undefined);
  const [bin, setBin] = React.useState<IDestinationBin>({
    name: '',
    barcode: '',
    sequence: undefined,
    isBin: true,
    stationType: undefined,
    departmentType: undefined,
  });

  const getStationsByDepartment = (departmentParam: string | undefined) => {
    if (departmentParam) {
      axios.get<ILabelEnumPair[]>('DestinationBin/GetStationTypesByDepartment', {
        params: {
          department: departmentParam,
        },
      })
        .then((response) => {
          if (response.data) {
            setStations([...response.data]);
          } else {
            setBin({ ...bin, stationType: undefined });
          }
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when fetching stations for department ${departmentParam}: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    }
  };

  const binFetcher = (url: string, binIdFetcherParam: string) => axios.get<IDestinationBin>(url, {
    params: {
      binId: binIdFetcherParam,
    },
  })
    .then((response) => {
      setBin({ ...response.data });
      getStationsByDepartment(response.data.departmentType?.label);
      return response.data;
    })
    .catch((e: AxiosError) => {
      enqueueSnackbar(`Redirecting to view bins page, there was the following error when fetching the bin: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });

  useSWR(
    binIdParam
      ? ['/DestinationBin/GetDestinationBin', binIdParam]
      : null,
    (url, barcodeParam) => binFetcher(url, barcodeParam), {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  const handleCreateBtnClicked = () => {
    if (!bin.name) {
      setShowErrors(true);
      enqueueSnackbar('Cannot submit, please fill in all required fields', { persist: false, variant: 'error' });
    } else if (bin.barcode === '') {
      setShowErrors(true);
      enqueueSnackbar('Cannot submit, barcode must be filled out', { persist: false, variant: 'error' });
    } else if (bin.stationType === undefined) {
      setShowErrors(true);
      enqueueSnackbar('Must select a station for the bin.', { persist: false, variant: 'error' });
    } else {
      axios.post('/DestinationBin/Upsert', {
        Id: binIdParam,
        Name: bin.name,
        Barcode: bin.barcode,
        StationType: bin.stationType,
        Sequence: bin.sequence,
      })
        .then(() => {
          enqueueSnackbar(`${binIdParam ? 'Updated' : 'Created'} bin: ${bin.name}`, { variant: 'success' });
          navigate('/locations');
        })
        .catch((error: AxiosError) => {
          if (error?.message) {
            enqueueSnackbar(`Unable to add/update destination bin, ${JSON.stringify(error.response?.data)}`, { persist: false, variant: 'error' });
          }
        });
    }
  };

  return (
    <Grid container spacing={0}>
      <Grid item xs={1} />
      <Grid item xs={10}>
        <Alert severity="info">
          {`${binIdParam ? 'Edit' : 'Create'} a Location (Destination Bin) by entering in the information below.`}
        </Alert>
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={1} />
      <Grid item xs={10}>
        <Box py={3}>
          <Card>
            <Paper>
              <CardHeader title={`${binIdParam ? 'Edit' : 'Create'} Location (Destination Bin)`} />
              <Divider variant="fullWidth" />
              <CardContent>
                <Grid container spacing={0}>
                  <Grid item xs={12} md={6}>
                    <Box p={2}>
                      <TextField
                        value={bin.name}
                        required
                        error={showErrors && !bin.name}
                        helperText={showErrors && !bin.name ? 'Bin Name is a required field' : ' '}
                        label="Name"
                        variant="outlined"
                        type="string"
                        fullWidth
                        onChange={(event) => {
                          const inputVal = event.target.value;
                          setBin({ ...bin, name: inputVal });
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box p={2}>
                      <TextField
                        value={bin.barcode}
                        required
                        label="Barcode"
                        error={(bin.barcode === '' && showErrors)}
                        helperText={(bin.barcode === '' && showErrors) ? 'Barcode is a required field' : ' '}
                        variant="outlined"
                        type="string"
                        fullWidth
                        onChange={(event) => {
                          const inputVal = event.target.value;
                          setBin({ ...bin, barcode: inputVal });
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Box px={2}>
                      {
                        (departments) ? (
                          <Autocomplete
                            value={departments.find((field) => field.value === bin.departmentType?.value) || { label: '', value: 0 }}
                            onChange={(e, data: ILabelEnumPair | null) => {
                              if (data) {
                                setBin({
                                  ...bin, stationType: undefined, departmentType: data,
                                });
                                setStations(undefined);
                                if (data.label !== '' || null) {
                                  getStationsByDepartment(data.label);
                                }
                              }
                            }}
                            autoHighlight
                            autoSelect
                            options={departments.sort((a, b) => a.label.localeCompare(b.label))}
                            getOptionLabel={(option) => option.label.toString()}
                            renderInput={(params) => (
                              <TextField
                                required
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...params}
                                id="department-type-input"
                                label="Department"
                                variant="outlined"
                                helperText={((bin.departmentType?.label === undefined || '') && showErrors) ? 'Must select a department for the bin.' : ' '}
                              />
                            )}
                          />
                        ) : (<TextField label="Department" id="department" disabled fullWidth variant="outlined" />)
                      }
                    </Box>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Box px={2}>
                      {
                  (stations && stations?.length > 0) ? (
                    <Autocomplete
                      value={stations.find((field) => field.value === bin.stationType?.value) || { label: '', value: 0 }}
                      onChange={(e, data: ILabelEnumPair | null) => {
                        if (data) {
                          setBin({ ...bin, stationType: data });
                        }
                      }}
                      autoHighlight
                      autoSelect
                      options={stations.sort((a, b) => a.label.localeCompare(b.label))}
                      getOptionLabel={(option) => option.label.toString()}
                      renderInput={(params) => (
                        <TextField
                          required
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...params}
                          id="station-type-input"
                          label="Station"
                          variant="outlined"
                          helperText={((bin.stationType?.label === undefined) && showErrors) ? 'Must select a station for the bin.' : ' '}
                        />
                      )}
                    />
                  ) : (<TextField label="Station" id="station" disabled fullWidth variant="outlined" />)
}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box pl={2} pr={2}>
                      <TextField
                        value={bin.sequence || ''}
                        label="Sequence"
                        variant="outlined"
                        type="number"
                        fullWidth
                        onChange={(event) => {
                          const inputVal = parseInt(event.target.value, 10);
                          setBin({ ...bin, sequence: inputVal });
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
              <Box py={2} px={2}>
                <Grid container direction="row-reverse" spacing={2}>
                  <Grid item>
                    <Button color="primary" variant="contained" onClick={handleCreateBtnClicked}>{`${binIdParam ? 'Edit' : 'Create'} Location (DESTINATION BIN)`}</Button>
                  </Grid>
                  <Grid item>
                    <Button color="secondary" onClick={() => navigate('/locations')}>{ 'CANCEL'}</Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Card>
        </Box>
      </Grid>
      <Grid item xs={1} />
    </Grid>
  );
};
export default UpsertBins;
